import { useCallback, useEffect, useMemo, useRef } from "react";

import clsx from "clsx";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { motion, AnimatePresence } from "framer-motion";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useMMMTableExpandedRows } from "@features/mmm/hooks/mmm-url-state";
import { useHiddenBudgetMixes } from "@features/mmm/hooks/use-hidden-budget-mixes";
import { aggregateBudgetMixSpendByPrimaryBreakdown } from "@features/mmm/utils/aggregate";
import { Carousel } from "@mantine/carousel";
import {
  type FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix,
  type FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix_data_summary_data,
} from "@north-beam/nb-client-side";
import {
  safeDivide,
  type BudgetMixData,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useAdoptedMix } from "@pages/mmm/hooks/use-adopted-mix";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { isScenarioModellingEnabled } from "@pages/mmm/tab-states";
import { Switch } from "@shared/switch";

import { BudgetMixCard, BudgetMixCardMode } from "../budget-mix-card/card";
import { CardWrapper } from "../budget-mix-card/card-wrapper";
import { CreateYourOwnMixCard } from "../budget-mix-card/create-your-own-mix-card";
import { BudgetMixErrorCard } from "../budget-mix-card/error-card";
import { HistoricalBudgetMixCard } from "../budget-mix-card/historical-card";
import {
  transformBauBudgetToHistoricalBudget,
  useReferencePerformance,
} from "../budget-mix-card/use-reference-performance";
import { BUDGET_MIX_WIDTH_WITH_CHART } from "../budget-mix-chart/constants";
import { useMaxPercentageSpendPerPrimaryBreakdown } from "../budget-mix-chart/hooks/use-max-percentage-spend-per-primary-breakdown";
import { BudgetMixControllerTable } from "../budget-mix-table/controller-table";
import { useMMMAdminMode } from "../budget-mix-table/utils";
import { useShowReferencePeriod } from "../hooks/use-show-reference-period";

import { historicalAndRecommendedDataAligner } from "./historical-and-recommended-data-aligner";

export type HistoricalBudgetMixResponse = Omit<
  FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix,
  "__typename" | "data"
> & {
  data: {
    spend_data: {
      breakdowns: { key: string; value: string }[];
      spend: number;
      data_column_name: string;
    }[];
    summary_data: Omit<
      FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix_data_summary_data,
      "__typename"
    >;
  };
};

type ProcessedMemoizedBudgetMixResponseAllData = {
  primaryBreakdown: string;
  historicalData: HistoricalBudgetMixResponse;
  recommendedData: FullBudgetMixResponse[];
  aggregatedSpendsByPrimaryBreakdown: {
    historical: Record<string, number>;
    recommended: Record<string, number>[];
  };
  shouldShowHistoricalData: true;
  shouldShowRecommendedData: true;
};

type ProcessedMemoizedBudgetMixResponseMissingData = {
  primaryBreakdown: string;
  historicalData?: HistoricalBudgetMixResponse | null;
  recommendedData: FullBudgetMixResponse[];
  aggregatedSpendsByPrimaryBreakdown: {
    historical: Record<string, number> | null;
    recommended: Record<string, number>[];
  };
  shouldShowHistoricalData: boolean;
  shouldShowRecommendedData: boolean;
};

type ProcessedMemoizedBudgetMixResponse =
  | ProcessedMemoizedBudgetMixResponseAllData
  | ProcessedMemoizedBudgetMixResponseMissingData;

export const BudgetMixCardContainer = ({
  data,
  mode,
  historical,
  loadingHistoricals,
  dateRange,
  openAddSpendDataModal,
}: {
  loadingHistoricals: boolean;
  data: BudgetMixData;
  historical: FetchHistoricalMixForCycle_me_mmm_cycle_historicalMix | undefined;
  mode: BudgetMixCardMode;
  dateRange: { startDate: string; endDate: string };
  openAddSpendDataModal: () => void;
}) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const [, setMaxPercentageSpendPerPrimaryBreakdown] =
    useMaxPercentageSpendPerPrimaryBreakdown();

  const [, setExpandedTableRows] = useMMMTableExpandedRows();
  const [hiddenBudgetMixes] = useHiddenBudgetMixes();

  const isAdminMode = useMMMAdminMode(user);
  const { selectedCycle } = useSelectedCycle();
  const [adoptedMixData] = useAdoptedMix();

  const enableMmmReferencePeriod = useShowReferencePeriod();

  const { bauBudget, isBauBudgetLoading, isForecastSpend, setIsForecastSpend } =
    useReferencePerformance();

  const isLoading = isForecastSpend ? isBauBudgetLoading : loadingHistoricals;

  const historicalRawData = useMemo(() => {
    if (historical) {
      if (bauBudget && isForecastSpend) {
        return transformBauBudgetToHistoricalBudget({
          bauBudget,
          historical,
        });
      }
    }

    return historical;
  }, [historical, bauBudget, isForecastSpend]);

  const recommendedRawData = useMemo(() => {
    const filtered = data.filter((datum) => {
      return !hiddenBudgetMixes.includes(String(datum.id));
    });
    filtered.sort((d1, d2) => d1.id - d2.id);
    return filtered as FullBudgetMixResponse[];
  }, [data, hiddenBudgetMixes]);

  const shouldShowCreateYourOwnMixCard = isScenarioModellingEnabled(
    selectedCycle,
    isAdminMode,
  );

  const {
    primaryBreakdown,
    historicalData,
    recommendedData,
    aggregatedSpendsByPrimaryBreakdown,
    shouldShowHistoricalData,
    shouldShowRecommendedData,
  } = useMemo((): ProcessedMemoizedBudgetMixResponse => {
    const historicalDataFound = !!historicalRawData;
    const recommendedDataFound = recommendedRawData.length > 0;
    const historicalDataBreakdownsMatchRecommendedDataBreakdowns = true;
    const shouldShowHistoricalDataLocal =
      historicalDataFound &&
      (historicalDataBreakdownsMatchRecommendedDataBreakdowns ||
        !recommendedDataFound);

    if (!shouldShowHistoricalDataLocal || !recommendedDataFound) {
      const historicalRawDataAggregatedSpend = historicalRawData?.data
        .spend_data
        ? aggregateBudgetMixSpendByPrimaryBreakdown(
            historicalRawData.data.spend_data,
          )
        : null;
      const recommendedDataAggregatedSpends = recommendedRawData.length
        ? recommendedRawData.map((d) =>
            aggregateBudgetMixSpendByPrimaryBreakdown(
              d.data.aggregated_data.spend_data,
            ),
          )
        : [];

      return {
        shouldShowRecommendedData:
          recommendedDataFound || shouldShowCreateYourOwnMixCard,
        shouldShowHistoricalData: shouldShowHistoricalDataLocal || isLoading,
        primaryBreakdown: selectedCycle.breakdowns[0],
        recommendedData: recommendedRawData,
        historicalData: historicalRawData,
        aggregatedSpendsByPrimaryBreakdown: {
          historical: historicalRawDataAggregatedSpend,
          recommended: recommendedDataAggregatedSpends,
        },
      };
    }
    return {
      shouldShowHistoricalData: shouldShowHistoricalDataLocal || isLoading,
      shouldShowRecommendedData: recommendedDataFound,
      primaryBreakdown: selectedCycle.breakdowns[0],
      ...historicalAndRecommendedDataAligner(
        historicalRawData,
        recommendedRawData,
      ),
    };
  }, [
    historicalRawData,
    recommendedRawData,
    isLoading,
    selectedCycle.breakdowns,
    shouldShowCreateYourOwnMixCard,
  ]);

  const recommendedMixesWithoutAdopted = useMemo(
    () => recommendedData.filter((i) => i.id !== adoptedMixData?.id),
    [recommendedData, adoptedMixData?.id],
  );

  const adoptedMix = useMemo(
    () => recommendedData.find((i) => i.id === adoptedMixData?.id),
    [recommendedData, adoptedMixData?.id],
  );

  useEffect(() => {
    /* PURPOSE: Set shared state of max percentage of spend per platform, so that second-level
       breakdowns can be shown with percentages correctly proportioned to each other.

       (1) Get single array of all Record<platformName, spend> from aggregatedSpendsByPlatform shape
       (2) Get corresponding array of totalSpends by summing values of each element in (1)
       (3) Create Record<platformName, maxPercentage> as an empty object
       (4) Iterate indices of len of array from (1), calculating the percentage using the
           corresponding index in array from (2). Populate Record (3) as you go.
       (5) Write record from (4) to atom which holds the max percentage spend per platform [used
           to offset 2nd-level-breakdown chart UI so that the percentage size are consistent across
           charts]
       (6) Initialize breakdown selection state to primary breakdown

       Note: Ternary handles unlikely data error case where current mix does not exist */
    const allSpendsPerPrimaryBreakdown =
      aggregatedSpendsByPrimaryBreakdown.historical &&
      Object.keys(aggregatedSpendsByPrimaryBreakdown.historical).length
        ? [
            aggregatedSpendsByPrimaryBreakdown.historical,
            ...aggregatedSpendsByPrimaryBreakdown.recommended,
          ]
        : [...aggregatedSpendsByPrimaryBreakdown.recommended];
    const allTotalSpends = allSpendsPerPrimaryBreakdown.map(
      (spendsPerPrimaryBreakdown) =>
        Object.values(spendsPerPrimaryBreakdown).reduce((a, b) => a + b),
    );
    const maxPercentageByPrimaryBreakdown: Record<string, number> = {};
    for (let i = 0; i < allSpendsPerPrimaryBreakdown.length; i++) {
      const spendPerPrimaryBreakdownRecord = allSpendsPerPrimaryBreakdown[i];
      const totalSpendForRecord = allTotalSpends[i];
      for (const [
        primaryBreakdownName,
        primaryBreakdownSpend,
      ] of Object.entries(spendPerPrimaryBreakdownRecord)) {
        const primaryBreakdownPercentage =
          safeDivide(primaryBreakdownSpend, totalSpendForRecord, {
            zeroOverZero: 0,
            infOverInf: 1,
          }) * 100;
        if (
          !maxPercentageByPrimaryBreakdown[primaryBreakdownName] ||
          maxPercentageByPrimaryBreakdown[primaryBreakdownName] <
            primaryBreakdownPercentage
        ) {
          maxPercentageByPrimaryBreakdown[primaryBreakdownName] =
            primaryBreakdownPercentage;
        }
      }
    }

    setMaxPercentageSpendPerPrimaryBreakdown(maxPercentageByPrimaryBreakdown);
  }, [
    aggregatedSpendsByPrimaryBreakdown,
    primaryBreakdown,
    setMaxPercentageSpendPerPrimaryBreakdown,
  ]);

  const renderHistoricalData = useCallback(
    () => (
      <>
        {!historicalData && isLoading && (
          <CardWrapper>
            <AnimatePresence>
              <motion.div
                className="absolute top-0 bottom-0 left-0 right-0 z-10 flex-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="absolute top-0 bottom-0 left-0 right-0 bg-v2-secondary opacity-60"></div>
                <img
                  className="h-24 z-1"
                  src={salesPlatformColumnLoading}
                  alt="Loading table"
                />
              </motion.div>
            </AnimatePresence>
            <div className="h-[45rem]"></div>
          </CardWrapper>
        )}
        {shouldShowHistoricalData &&
          historicalData &&
          mode === BudgetMixCardMode.TABLE && (
            <div className="flex flex-col justify-start m-0 p-0">
              <div
                className={clsx("w-full m-0 p-0 ", {
                  "min-h-[230px] h-[230px]": !enableMmmReferencePeriod,
                  "min-h-[284.75px] h-[284.75px]": enableMmmReferencePeriod,
                })}
              />
              <BudgetMixControllerTable
                tableData={historicalData.data.spend_data}
                onExpandRows={(value) => {
                  trackMMMEvent({
                    component: ExploreBudgetsTrackedComponent.BUDGET_MIX_CARD,
                    action: "onChange",
                    actionInfo: "Change expanded budget mix table rows",
                  });
                  setExpandedTableRows(value);
                }}
              />
            </div>
          )}
        {shouldShowHistoricalData && historicalData && (
          <div>
            <div className="h-8 flex items-center justify-between">
              <div className="text-gray+1 font-semibold p-1">
                Historical Comparison
              </div>
              {enableMmmReferencePeriod && (
                <div className="flex items-center gap-1.5 text-2xs font-bold tracking-wide">
                  <span className="text-interstellar-1">HISTORICAL ACTUAL</span>
                  <Switch
                    id="actual-spend-switch"
                    value={isForecastSpend}
                    onChange={setIsForecastSpend}
                    size="x-small"
                  />
                  <span className="text-interstellar-1">
                    FORECAST
                    <Tooltip
                      content={
                        <div className="w-56">
                          A forecasted media mix based on average weekly channel
                          spend during the historical period.
                        </div>
                      }
                    ></Tooltip>
                  </span>
                </div>
              )}
            </div>
            <HistoricalBudgetMixCard
              chartData={historicalData.data.spend_data}
              mode={mode}
              spend={historicalData.data.summary_data.spend}
              loading={isLoading}
              kpiData={historicalData.data.summary_data.kpi_data}
              historicalData={historicalData}
            />
          </div>
        )}
      </>
    ),
    [
      enableMmmReferencePeriod,
      historicalData,
      isForecastSpend,
      isLoading,
      mode,
      setExpandedTableRows,
      setIsForecastSpend,
      shouldShowHistoricalData,
      trackMMMEvent,
    ],
  );

  const renderAdoptedMix = useCallback(
    (mix: FullBudgetMixResponse) => {
      const historicalComparisonData = {
        kpi_data: historicalData?.data.summary_data.kpi_data,
        spend: historicalData?.data.summary_data.spend,
      };
      return (
        <div className="w-full inline-block">
          <div className="h-8">
            <span className={clsx("font-semibold p-1 text-alien+0")}>
              Adopted Mix
            </span>
            <span>
              <i className="fa-solid fa-check text-alien+0"></i>
            </span>
          </div>
          <BudgetMixCard
            budgetMix={mix}
            budgetMixId={String(mix.id)}
            chartData={mix.data.aggregated_data.spend_data}
            summaryData={mix.data.aggregated_data.summary_data}
            clientSettingId={mix.client_setting_id}
            mode={mode}
            loading={isLoading}
            historicalComparisonData={historicalComparisonData}
            isAdopted={true}
            mixIndex={0}
            dateRange={dateRange}
          />
        </div>
      );
    },
    [
      dateRange,
      historicalData?.data.summary_data.kpi_data,
      historicalData?.data.summary_data.spend,
      isLoading,
      mode,
    ],
  );

  const wheelGestures = useRef(WheelGesturesPlugin());

  const renderRecommendedData = () => (
    <div className="flex-grow inline-block overflow-auto">
      <div className="h-8">
        <span
          className={clsx("text-gray+1 font-semibold p-1", {
            hidden: mode === BudgetMixCardMode.TABLE,
          })}
        >
          Additional mixes
        </span>
      </div>
      <Carousel
        classNames={{
          container: "space-x-4 h-full",
          control:
            "data-[inactive]:opacity-0 data-[inactive]:cursor-default bg-cosmic text-white h-12 w-12 rounded-full text-xl [--mantine-scale:1.75]",
          controls: "opacity-0 top-0 bottom-0 mx-4 transition-opacity",
          root: "[&:hover_.mantine-Carousel-controls]:opacity-100 h-[calc(100%-2rem)]",
          viewport: "h-full",
        }}
        plugins={[wheelGestures.current]}
        slideSize={BUDGET_MIX_WIDTH_WITH_CHART}
        inViewThreshold={1}
        includeGapInSize
        dragFree
        withControls={recommendedMixesWithoutAdopted.length > 1}
        align="start"
        loop={false}
      >
        {recommendedMixesWithoutAdopted.map((datum, i) => {
          const historicalComparisonData = {
            kpi_data: historicalData?.data.summary_data.kpi_data,
            spend: historicalData?.data.summary_data.spend,
          };
          return (
            <Carousel.Slide key={datum.id}>
              <BudgetMixCard
                budgetMix={datum}
                budgetMixId={String(datum.id)}
                chartData={datum.data.aggregated_data.spend_data}
                summaryData={datum.data.aggregated_data.summary_data}
                clientSettingId={datum.client_setting_id}
                mode={mode}
                loading={isLoading}
                historicalComparisonData={historicalComparisonData}
                isAdopted={false}
                mixIndex={i}
                dateRange={dateRange}
              />
            </Carousel.Slide>
          );
        })}
        {shouldShowCreateYourOwnMixCard && (
          <Carousel.Slide>
            <CreateYourOwnMixCard
              openAddSpendDataModal={openAddSpendDataModal}
            />
          </Carousel.Slide>
        )}
      </Carousel>
    </div>
  );

  // Render error state if necessary data doesn't exist
  if (!shouldShowHistoricalData || !shouldShowRecommendedData) {
    if (!shouldShowHistoricalData && !shouldShowRecommendedData) {
      // NOTE (dan, 2024-04-05) this used to throw an error; making this not throw an error so as to not crash the entire app
      return <div className="flex justify-start gap-4">No data</div>;
    }
    if (shouldShowHistoricalData || isLoading) {
      return (
        <div className="flex justify-start gap-4">{renderHistoricalData()}</div>
      );
    }
  }

  return (
    <div className="flex justify-start gap-4 w-full">
      <div className="flex justify-start w-fit h-fit">
        {renderHistoricalData()}
      </div>
      {adoptedMix && (
        <>
          <div className="border border-v2-secondary h-full"></div>
          <div className="flex justify-start w-fit h-fit">
            {renderAdoptedMix(adoptedMix)}
          </div>
        </>
      )}
      <div className="border border-v2-secondary h-full"></div>
      {renderRecommendedData()}
    </div>
  );
};
